import { useRoute } from 'vue-router'

import { useAuthStore } from '../../stores/authStore.js'
import { useScopeStore } from '../../stores/scopeStore.js'

import scopeUtility from '../../utils/scope.utility.js'
import pathUtility from '../../utils/path.utility.js'
import orgGroupScopeUtility from '../../utils/orgGroupScope.utility.js'
import ticketGroupScopeUtility from '../../utils/ticketGroupScope.utility.js'
import ticketSettingsHostUtility from '../../utils/ticketSettingsHost.utility.js'

/**
 * Build cache `meta` bags for ticket list and settings loads from the
 * current scope + session generations.
 */
export function useTicketMeta() {
  const route = useRoute()
  const authStore = useAuthStore()
  const scopeStore = useScopeStore()

  /**
   * Meta for active / archived ticket list slices.
   *
   * @param {string[]} [ticketGroupIds] Group-mode ticket-group filter (route query).
   * @returns {{ scopeKey: string, ids: string[], domain: string, generations: object }}
   */
  function listMeta(ticketGroupIds = []) {
    const orgGroupId = orgGroupScopeUtility.parseId(route.query)
    const groupKey = ticketGroupIds.length
      ? `:tg:${[...ticketGroupIds].map(String).sort().join(',')}`
      : ''
    const orgKey = orgGroupId ? `:og:${orgGroupId}` : ''

    let ids = scopeUtility.ids(scopeStore.scope)
    if (ticketGroupIds.length) {
      const groupAccountIds = ticketGroupScopeUtility.accountIds(
        ticketGroupIds,
        authStore.user?.ticketGroups,
        authStore.user?.allowedTicketAccountIds,
      )
      if (groupAccountIds.length) {
        const allowed = new Set(groupAccountIds.map(String))
        ids = ids.map(String).filter((id) => allowed.has(id))
      } else {
        ids = []
      }
    }

    return {
      scopeKey: scopeUtility.key(scopeStore.scope) + groupKey + orgKey,
      ids,
      domain: 'tickets',
      generations: authStore.user?.accountGenerations ?? {},
    }
  }

  /**
   * Meta for merged settings-host loads (account or ticket group tree).
   *
   * @param {string|null} [ticketGroupId]
   * @returns {{ scopeKey: string|null, ids: string[], domain: string, generations: object, apiScope: string|null }}
   */
  function settingsMeta(ticketGroupId = null) {
    const host = scopeUtility.resolve.host({
      scope: scopeStore.scope,
      user: authStore.user,
      ticketGroupId,
    })
    return {
      scopeKey: host.scopeKey,
      ids: host.ids,
      domain: 'tickets',
      generations: authStore.user?.accountGenerations ?? {},
      apiScope: host.apiScope,
    }
  }

  /**
   * Cache meta for a concrete settings host key (`TicketGroup:<id>` or
   * `TicketAccount:<id>`) — used by the ticket editor so dropdown options
   * match the owning ticket's workflow host, not the page scope bar.
   *
   * @param {string|null|undefined} hostKey
   * @param {object|null|undefined} [user]
   * @returns {{ scopeKey: string, ids: string[], domain: string, generations: object, apiScope: string|null, ticketGroupId?: string }|null}
   */
  function settingsMetaForHostKey(hostKey, user = authStore.user) {
    if (!hostKey || !user) return null

    const base = {
      domain: 'tickets',
      generations: user.accountGenerations ?? {},
    }

    if (hostKey.startsWith('TicketGroup:')) {
      const groupId = hostKey.slice('TicketGroup:'.length)
      const group = ticketSettingsHostUtility.findGroup(user, groupId)
      const ids = group
        ? ticketSettingsHostUtility.accountIdsInGroup(group)
        : []
      const fallbackAccountId = user.currentAccount?._id ?? user.currentAccount
      const resolvedIds = ids.length ? ids : fallbackAccountId ? [String(fallbackAccountId)] : []
      // Collaborators use the group settings API; account-only members (group not
      // in session) still get a meta bag so the editor can load via account API.
      if (!group && !resolvedIds.length) return null
      return {
        ...base,
        scopeKey: hostKey,
        ids: resolvedIds,
        apiScope: group
          ? pathUtility.scope({ type: 'group', groupId })
          : pathUtility.scope({ type: 'account' }),
        ticketGroupId: groupId,
      }
    }

    if (hostKey.startsWith('TicketAccount:')) {
      const accountId = hostKey.slice('TicketAccount:'.length)
      if (!accountId) return null
      return {
        ...base,
        scopeKey: hostKey,
        ids: [accountId],
        apiScope: pathUtility.scope({ type: 'account' }),
      }
    }

    return null
  }

  return { listMeta, settingsMeta, settingsMetaForHostKey }
}
